import {
  ConnectBankStatuses,
  EnablePayoutStatuses,
  InternalBankStatuses,
  PaymentSystem,
  PayoutScheduleType,
  PayoutType
} from '../constants';

export type PayoutScheduleAboutCopy = {
  text: string;
  subText: string;
};

export type PayoutScheduleViewState =
  | { kind: 'WA_RECONNECT_PAUSE' }
  | { kind: 'ACTION_REQUIRED_PAUSE' }
  | { kind: 'IN_REVIEW_PAUSE' }
  | { kind: 'BANK_NOT_LINKED' }
  | { kind: 'KYC_ACTION_REQUIRED' }
  | { kind: 'RISK_ADDITIONAL_DATA_REQUIRED' }
  | { kind: 'RISK_REVIEW_IN_PROGRESS'; onboardingDataLastSubmittedOn: string | null | undefined }
  | { kind: 'RISK_HOLD' }
  | { kind: 'GENERIC_PAUSE'; contactOwner: boolean }
  | { kind: 'AUTO_DAILY' }
  | {
      kind: 'ACTIVE_SCHEDULE';
      payoutType: PayoutType;
      payoutSchedule: PayoutScheduleType.INSTANT | PayoutScheduleType.DAILY;
    };

export type PayoutScheduleViewStateInput = {
  bankingEnabled: boolean;
  payoutSchedule: PayoutScheduleType;
  payoutType: PayoutType;
  hasActionRequiredTeamBankAccount: boolean;
  hasInReviewTeamBankAccount: boolean;
  isOwner: boolean;
  paymentSystem: PaymentSystem | string | null | undefined;
  merchantCustomizationsLoaded: boolean;
  hasWalletEligibleBankAccount: boolean;
  isLegalAgreementReAcceptanceRequired: boolean;
  connectBankingStatus: string | null | undefined;
  internalBankingStatus: string | null | undefined;
  connectApplicationStatus: string | null | undefined;
  isAdditionalDataRequiredForRisk: boolean;
  isRiskReviewDone: boolean;
  isHoldAndReleaseEnabled: boolean;
  onboardingDataLastSubmittedOn: string | null | undefined;
};

const GENERIC_ABOUT_TEXT = 'Your payout schedule is how often ScanPay sends money to your linked bank account.';

export const getPayoutScheduleAboutCopy = (payoutSchedule: PayoutScheduleType): PayoutScheduleAboutCopy => {
  if (payoutSchedule === PayoutScheduleType.OFF) {
    return { text: GENERIC_ABOUT_TEXT, subText: '' };
  }

  if (payoutSchedule === PayoutScheduleType.INSTANT) {
    return {
      text: GENERIC_ABOUT_TEXT,
      subText: 'You are currently receiving instant payouts after every payment.'
    };
  }

  return {
    text: GENERIC_ABOUT_TEXT,
    subText: `For automatic daily payouts, ScanPay sends a payout for all transactions
        occurred before 7pm ET for the day. Payouts are sent via standard ACH which reaches 
        your bank account within 2 business days.`
  };
};

export const getPayoutScheduleViewState = (input: PayoutScheduleViewStateInput): PayoutScheduleViewState => {
  const {
    bankingEnabled,
    payoutSchedule,
    payoutType,
    hasActionRequiredTeamBankAccount,
    hasInReviewTeamBankAccount,
    isOwner,
    paymentSystem,
    merchantCustomizationsLoaded,
    hasWalletEligibleBankAccount,
    isLegalAgreementReAcceptanceRequired,
    connectBankingStatus,
    internalBankingStatus,
    connectApplicationStatus,
    isAdditionalDataRequiredForRisk,
    isRiskReviewDone,
    isHoldAndReleaseEnabled,
    onboardingDataLastSubmittedOn
  } = input;

  const isOff = payoutSchedule === PayoutScheduleType.OFF;

  const isWaReconnectPause =
    isOff &&
    bankingEnabled &&
    isOwner &&
    merchantCustomizationsLoaded &&
    paymentSystem === PaymentSystem.WA &&
    !hasWalletEligibleBankAccount &&
    !isLegalAgreementReAcceptanceRequired &&
    !hasInReviewTeamBankAccount;

  if (isWaReconnectPause) {
    return { kind: 'WA_RECONNECT_PAUSE' };
  }

  if (isOff && hasActionRequiredTeamBankAccount) {
    return { kind: 'ACTION_REQUIRED_PAUSE' };
  }

  if (isOff && hasInReviewTeamBankAccount) {
    return { kind: 'IN_REVIEW_PAUSE' };
  }

  if (!bankingEnabled) {
    return { kind: 'BANK_NOT_LINKED' };
  }

  const isBankSubmittedAndVerified =
    connectBankingStatus === ConnectBankStatuses.SUBMITTED &&
    internalBankingStatus === InternalBankStatuses.FINANCIAL_DATA_VERIFIED;

  const isKycActionRequired =
    connectApplicationStatus === EnablePayoutStatuses.SUBMITTED_NEED_MORE_PAYMENT_ALLOWED ||
    connectApplicationStatus === EnablePayoutStatuses.SUBMITTED_NEED_MORE_PAYMENT_NOT_ALLOWED;

  if (isOff && isBankSubmittedAndVerified && isKycActionRequired) {
    return { kind: 'KYC_ACTION_REQUIRED' };
  }

  if (isOff && isAdditionalDataRequiredForRisk) {
    return { kind: 'RISK_ADDITIONAL_DATA_REQUIRED' };
  }

  if (isOff && !isRiskReviewDone && !isAdditionalDataRequiredForRisk) {
    return { kind: 'RISK_REVIEW_IN_PROGRESS', onboardingDataLastSubmittedOn };
  }

  if (isHoldAndReleaseEnabled) {
    return { kind: 'RISK_HOLD' };
  }

  if (isOff) {
    return {
      kind: 'GENERIC_PAUSE',
      contactOwner: paymentSystem === PaymentSystem.WA && !isOwner
    };
  }

  if (payoutSchedule === PayoutScheduleType.DAILY) {
    return { kind: 'AUTO_DAILY' };
  }

  return {
    kind: 'ACTIVE_SCHEDULE',
    payoutType,
    payoutSchedule: payoutSchedule as PayoutScheduleType.INSTANT | PayoutScheduleType.DAILY
  };
};
